import type { FC, ReactElement } from 'react';
import { useEffect } from 'react';

import { Logger } from '../../../logger/Logger';
import { L10nText } from '../../../services/uiTranslations/reactBindings/L10nText';
import { CoreDispatch, useCoreDispatch } from '../../../store/CoreStore';
import { useAutoplaySlice } from '../../../store/slices/autoplay/selectors';
import { autoplayActions } from '../../../store/slices/autoplay/slice';
import { useBetSlice } from '../../../store/slices/bets/selectors';
import { popupActions } from '../../../store/slices/popup/slice';
import { toastActions } from '../../../store/slices/toast/slice';
import { ButtonVariant } from '../../controls/button/Button';
import { CurrencyConnected } from '../../currency/CurrencyConnected';
import { validateAndSaveBets } from '../../game/gameControls/autoplayControl/utils/validateAndSaveBets';
import { PopupType } from '../../popup/types';

import { BlockingModalActionButton, BlockingModalContent } from './BlockingModalContent';

export const AutoplayModifiedConnected: FC = (): ReactElement => {
  const dispatch = useCoreDispatch();
  const currentBet = useBetSlice.bets();
  const acceptedBets = useBetSlice.acceptedBets();
  const invalidBets = useBetSlice.invalidBetspots();
  const totalBet = useBetSlice.totalBet();
  const autoplayInProgress = useAutoplaySlice.isInProgress();

  useEffect(() => {
    if (!autoplayInProgress) {
      /** When autoplay is running and you do visibility change popup is closed and toaster is to be showed */
      dispatch(popupActions.dismissPopup(PopupType.AutoplayModified));
      dispatch(toastActions.createToast('AUTOPLAY_CONTINUED_NO_CHANGES'));
    }
  }, [autoplayInProgress, dispatch]);

  const action: BlockingModalActionButton[] = [
    {
      tk: 'THIS_ROUND_ONLY',
      type: ButtonVariant.MAIN,
      click: (): void => {
        dispatch(toastActions.createToast('AUTOPLAY_CONTINUED_NO_CHANGES'));
        dispatch(popupActions.dismissPopup(PopupType.AutoplayModified));
        Logger.logEvent('autoplay_modify', {
          additional: 'Popup_ThisRound',
        });
      },
    },
    {
      tk: 'MODIFY',
      type: ButtonVariant.ACCENT,
      click: (): void => {
        if (currentBet) {
          validateAndSaveBets({
            dispatch: dispatch,
            currentBet: currentBet,
            acceptedBets: acceptedBets,
            invalidBets: invalidBets,
          });
        }
        dispatch(popupActions.dismissPopup(PopupType.AutoplayModified));
        dispatch(toastActions.createToast('AUTOPLAY_BET_AMOUNT_MODIFIED'));
        dispatch(autoplayActions.setActiveBetAmount(totalBet));
        Logger.logEvent('autoplay_modify', {
          additional: 'Popup_AllRounds',
        });
      },
    },
  ];
  const messageContent = (
    <>
      <L10nText tk="NEW_BET_AMOUNT" /> <CurrencyConnected value={totalBet} />
      <div style={{ paddingTop: '8px' }}>
        <L10nText tk="MODIFY_AUTOPLAY_WITH_NEW_BET" />
      </div>
    </>
  );

  return <BlockingModalContent titleTk="MODIFY_AUTOPLAY" messageComponent={messageContent} actions={action} />;
};

export const autoplayModifiedConnectedOnClose = (dispatch: CoreDispatch): void => {
  dispatch(toastActions.createToast('AUTOPLAY_CONTINUED_NO_CHANGES'));
};
